import { createContext, useContext, ReactNode, useMemo, useCallback } from 'react';
import { ApolloError } from '@apollo/client';
import {
    type SubscriptionOrgFragment,
    useSubscriptionCalculations,
    type UseSubscriptionCalculationsResult,
} from './hooks/subscription.hook';
import { useSubscriptionData } from './hooks/subscription.data';

interface SubscriptionContextValue extends UseSubscriptionCalculationsResult {
    organization?: SubscriptionOrgFragment | null;
    loading: boolean;
    error?: ApolloError;
    refetch: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
    const { organization, loading, error, refetch } = useSubscriptionData();

    const stableRefetch = useCallback(async () => {
        await refetch();
    }, [refetch]);

    const calculations = useSubscriptionCalculations(organization);

    const contextValue = useMemo<SubscriptionContextValue>(
        () => ({
            organization,
            loading,
            error,
            refetch: stableRefetch,
            ...calculations,
        }),
        [organization, loading, error, stableRefetch, calculations]
    );

    return (
        <SubscriptionContext.Provider value={contextValue}>{children}</SubscriptionContext.Provider>
    );
}

export function useSubscriptionOrganization(): SubscriptionContextValue {
    const context = useContext(SubscriptionContext);
    if (!context) {
        throw new Error('useSubscriptionOrganization must be used within a SubscriptionProvider');
    }
    return context;
}
